import { addDays, assertLocalDay } from './dates';
import type { LocalDay, Money, RecurrenceRule } from './model';

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function addMonths(anchor: LocalDay, amount: number): LocalDay {
  const [year, month, day] = anchor.split('-').map(Number);
  const index = year! * 12 + (month! - 1) + amount;
  const resultYear = Math.floor(index / 12);
  const resultMonth = (index % 12 + 12) % 12 + 1;
  const resultDay = Math.min(day!, daysInMonth(resultYear, resultMonth));
  return `${resultYear}-${String(resultMonth).padStart(2, '0')}-${String(resultDay).padStart(2, '0')}`;
}

function addYears(anchor: LocalDay, amount: number): LocalDay {
  const [year, month, day] = anchor.split('-').map(Number);
  const resultYear = year! + amount;
  const resultDay = Math.min(day!, daysInMonth(resultYear, month!));
  return `${resultYear}-${String(month).padStart(2, '0')}-${String(resultDay).padStart(2, '0')}`;
}

export function recurrenceOccurrence(rule: Pick<RecurrenceRule, 'anchor' | 'cadence' | 'interval'>, index: number): LocalDay {
  if (index < 0) throw new Error('Índice de recorrência inválido.');
  const interval = Math.max(1, rule.interval);
  if (rule.cadence === 'weekly') return addDays(rule.anchor, index * interval * 7);
  if (rule.cadence === 'monthly') return addMonths(rule.anchor, index * interval);
  return addYears(rule.anchor, index * interval);
}

export function recurrenceOccurrences(rule: Pick<RecurrenceRule, 'anchor' | 'cadence' | 'interval' | 'endOn' | 'occurrenceCount'>, horizon: LocalDay): LocalDay[] {
  assertLocalDay(horizon);
  const maximum = Math.min(rule.occurrenceCount ?? 2_000, 2_000);
  const result: LocalDay[] = [];
  for (let index = 0; index < maximum; index += 1) {
    const candidate = recurrenceOccurrence(rule, index);
    if (candidate > horizon || (rule.endOn && candidate > rule.endOn)) break;
    result.push(candidate);
  }
  return result;
}

export function installmentAmounts(total: Money, count: number): Money[] {
  if (!Number.isInteger(count) || count < 2 || count > 600 || total < BigInt(count)) return [];
  const quotient = total / BigInt(count);
  const remainder = total % BigInt(count);
  return Array.from({ length: count }, (_, index) => quotient + (BigInt(index) < remainder ? 1n : 0n));
}

export async function recurrenceOccurrenceID(ruleID: string, day: LocalDay): Promise<string> {
  assertLocalDay(day);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${ruleID}:${day}`))).slice(0, 16);
  digest[6] = (digest[6]! & 0x0f) | 0x80;
  digest[8] = (digest[8]! & 0x3f) | 0x80;
  const value = Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}
